/**
 * feedback.ts — the single home for transient in-app feedback (antd toasts).
 *
 * WHY THIS EXISTS
 * ---------------
 * Operators reported two problems with the old toasts: they disappeared before
 * anyone could read them ("za szybko znikają"), and they said nothing useful
 * ("bardzo vague — nie wiadomo o co chodzi"). The global antd default was 3s for
 * every severity, so a blocking error vanished as fast as a routine save.
 *
 * DURATION POLICY (seconds)
 * -------------------------
 *   success  5  — confirmation; the operator already expects it, but the toast
 *                 usually carries the created/changed document number, so it
 *                 must stay readable long enough to note it down.
 *   info     5  — same reading load as success, no action required.
 *   warning  8  — the operator has to decide or redo something. Longer.
 *   error    8  — the operator has to understand WHY it failed before retrying.
 *
 * Anything the operator MUST act on before continuing (a blocked destructive
 * operation, a guard that stops a document from being issued) may pass
 * `{ sticky: true }`. Sticky toasts never auto-dismiss; they close when the
 * operator clicks them (antd `message` has no close button of its own, so
 * `createFeedback` wires the click). Use them sparingly — a screen full of
 * toasts that never leave is its own usability bug.
 *
 * `App.tsx` sets the global antd default to FEEDBACK_DEFAULT_DURATION as a
 * safety net: every existing call site goes through this module, and any future
 * stray still gets a readable 5s instead of the old 3s.
 *
 * COPY POLICY (Polish-only product surface)
 * -----------------------------------------
 * - Success names the object: "Utworzono PZ/55/08/2026", not "Zapisano".
 * - Errors say what failed AND why. When the backend returns a code or a
 *   validation reason, map it (see `getApiErrorMessage` in `apiError.ts` and the
 *   per-module code→sentence mappers such as
 *   `modules/transports/transportRouteViews.tsx`). A generic catch-all must at
 *   minimum name the operation that failed.
 * - No raw backend codes, no English, no bare "Błąd".
 *
 * USAGE
 * -----
 *   const feedback = useFeedback();
 *   feedback.success(`Utworzono ${pzNumber}`);
 *   feedback.error('Nie udało się wydać WZ — brak stanu magazynowego', { sticky: true });
 */
import { App } from 'antd';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { MessageInstance, MessageType } from 'antd/es/message/interface';

export type FeedbackSeverity = 'success' | 'info' | 'warning' | 'error';

/** Auto-dismiss delay per severity, in seconds. See the policy in the header. */
export const FEEDBACK_DURATIONS: Record<FeedbackSeverity, number> = {
  success: 5,
  info: 5,
  warning: 8,
  error: 8,
};

/** antd treats duration 0 as "never auto-close". */
export const FEEDBACK_STICKY_DURATION = 0;

/**
 * Global antd fallback for call sites that still use `App.useApp().message`
 * directly. Deliberately the success/info value: it is the safe middle ground
 * for an unclassified toast.
 */
export const FEEDBACK_DEFAULT_DURATION = FEEDBACK_DURATIONS.success;

/**
 * Toast placement (ZK card usability round 2026-10-05): below the sticky
 * 64 px app header so a toast never covers the page title or its actions,
 * and at most three on screen — an older one gives way to a newer one.
 * Screens with steps also close their toasts when the operator moves on
 * (`useScopedFeedback().clear()` on a step change; sticky toasts stay), so a toast never stays
 * from a previous action.
 * A sticky toast is therefore reserved for what outlives the action; a save error of a form
 * uses one fixed `key` (a new attempt replaces it) and closes itself — it is closed explicitly
 * on a step change and when the screen unmounts (`ORDER_DRAFT_SAVE_ERROR_KEY`).
 */
export const FEEDBACK_TOP_OFFSET = 76;
export const FEEDBACK_MAX_COUNT = 3;

export interface FeedbackOptions {
  /** Keep the toast on screen until the operator dismisses it. */
  sticky?: boolean;
  /** Explicit override in seconds. Wins over `sticky` and the severity default. */
  duration?: number;
  /** antd message key — reuse to replace an existing toast instead of stacking. */
  key?: string;
}

/**
 * Resolves the effective duration for a toast. Exported so the policy can be
 * unit-tested without mounting React.
 *
 * Precedence: explicit `duration` > `sticky` > severity default.
 */
export function resolveFeedbackDuration(
  severity: FeedbackSeverity,
  options?: FeedbackOptions
): number {
  if (typeof options?.duration === 'number') return options.duration;
  if (options?.sticky) return FEEDBACK_STICKY_DURATION;
  return FEEDBACK_DURATIONS[severity];
}

export type FeedbackFn = (content: ReactNode, options?: FeedbackOptions) => MessageType;

export interface FeedbackApi {
  success: FeedbackFn;
  info: FeedbackFn;
  warning: FeedbackFn;
  error: FeedbackFn;
  /**
   * Escape hatch to the raw antd message instance for the rare call that needs
   * `open`/`destroy`. Prefer the severity helpers — they carry the policy.
   */
  message: MessageInstance;
}

/**
 * Builds the severity helpers around an antd message instance. Exported for
 * tests and for the handful of non-hook contexts that already hold an instance.
 */
// Module-wide, not per instance: every component holds its own `createFeedback`
// over the one antd message host, and a repeated key would replace another
// screen's sticky toast.
let stickyCounter = 0;

export function createFeedback(message: MessageInstance): FeedbackApi {
  const emit = (severity: FeedbackSeverity): FeedbackFn => (content, options) => {
    const duration = resolveFeedbackDuration(severity, options);
    if (duration !== FEEDBACK_STICKY_DURATION) {
      return message[severity]({
        content,
        duration,
        ...(options?.key ? { key: options.key } : {}),
      });
    }
    // A toast that never closes by itself must close on a click — otherwise it
    // stays until the page reloads (ZK card, status refusal 2026-10-20).
    stickyCounter += 1;
    const key = options?.key ?? `feedback-sticky-${stickyCounter}`;
    return message[severity]({
      content,
      duration,
      key,
      onClick: () => message.destroy(key),
      style: { cursor: 'pointer' },
    });
  };

  return {
    success: emit('success'),
    info: emit('info'),
    warning: emit('warning'),
    error: emit('error'),
    message,
  };
}

/**
 * Feedback for components. Wraps `App.useApp().message`, so it inherits the
 * React 19-safe context instance — never import the static `message` export.
 */
export function useFeedback(): FeedbackApi {
  const { message } = App.useApp();
  return useMemo(() => createFeedback(message), [message]);
}

export interface ScopedFeedbackApi extends FeedbackApi {
  /**
   * Closes the non-sticky toasts this screen raised (a step change, a new
   * action, leaving the screen). Sticky toasts — a blocking error, a WZ saved
   * as a draft — stay until the operator closes them.
   */
  clear: () => void;
}

/**
 * Feedback of one screen whose toasts belong to its current action (ZK card,
 * usability round 2026-10-05). Each non-sticky toast gets a `${scope}-n` key
 * so `clear()` closes only those, never another screen's or a sticky one.
 */
export function createScopedFeedback(message: MessageInstance, scope: string): ScopedFeedbackApi {
  const base = createFeedback(message);
  const keys = new Set<string>();
  let counter = 0;
  const emit = (severity: FeedbackSeverity): FeedbackFn => (content, options) => {
    const sticky = resolveFeedbackDuration(severity, options) === FEEDBACK_STICKY_DURATION;
    if (sticky || options?.key) return base[severity](content, options);
    counter += 1;
    const key = `${scope}-${counter}`;
    keys.add(key);
    return base[severity](content, { ...options, key });
  };
  return {
    ...base,
    success: emit('success'),
    info: emit('info'),
    warning: emit('warning'),
    error: emit('error'),
    clear: () => {
      keys.forEach((key) => message.destroy(key));
      keys.clear();
    },
  };
}

export function useScopedFeedback(scope: string): ScopedFeedbackApi {
  const { message } = App.useApp();
  return useMemo(() => createScopedFeedback(message, scope), [message, scope]);
}
